/// <reference types="google.maps" />
import { calculatePropertyToEmployerDistance } from './distanceMatrix'
import { waitForGoogleMaps } from './googleMaps'

export type DistanceCalculationMethod = 'driving' | 'straight_line' | 'none'

export interface EmploymentDistanceResult {
  miles: number | null
  method: DistanceCalculationMethod
}

export interface EmployerAddressFields {
  streetName?: string
  city?: string
  state?: string
  zipCode?: string
  address?: string
}

/** Build property address from application payload (matches Income tab field order). */
export function resolvePropertyAddressFromApplication(
  applicationData: Record<string, unknown> | null | undefined,
): string {
  if (!applicationData) {
    return ''
  }

  const propertyData = (applicationData.property as Record<string, unknown>) || {}

  const propAddress =
    (applicationData.property_address as string) ||
    (applicationData.physicalAddress as string) ||
    (propertyData.property_address as string) ||
    (propertyData.address as string) ||
    ''
  const propCity =
    (applicationData.property_city as string) ||
    (propertyData.property_city as string) ||
    (propertyData.city as string) ||
    ''
  const propState =
    (applicationData.property_state as string) ||
    (propertyData.property_state as string) ||
    (propertyData.state as string) ||
    ''
  const propZipcode =
    (applicationData.property_zipcode as string) ||
    (propertyData.property_zipcode as string) ||
    (propertyData.zipcode as string) ||
    (propertyData.zip_code as string) ||
    ''

  if (!propAddress) {
    return ''
  }

  if (propCity || propState || propZipcode) {
    return `${propAddress} ${propCity} ${propState} ${propZipcode}`.trim()
  }

  return propAddress.trim()
}

export function hasCompleteEmployerAddress(
  employer: EmployerAddressFields,
): boolean {
  return Boolean(
    employer.streetName?.trim() &&
      employer.city?.trim() &&
      employer.state?.trim() &&
      employer.zipCode?.trim(),
  )
}

/** Canonical employer address string used for Distance Matrix (street + city + state + zip). */
export function buildEmployerAddressString(
  employer: EmployerAddressFields,
): string {
  if (hasCompleteEmployerAddress(employer)) {
    return `${employer.streetName} ${employer.city} ${employer.state} ${employer.zipCode}`.trim()
  }

  return employer.address?.trim() || ''
}

export function normalizeAddressForComparison(address: string): string {
  return address.trim().toLowerCase().replace(/\s+/g, ' ')
}

export function hasValidJobDistance(distance?: number | null): boolean {
  return (
    distance !== undefined && distance !== null && !Number.isNaN(distance)
  )
}

export function parseJobDistanceFromApi(
  value: number | string | null | undefined,
): number | undefined {
  if (typeof value === 'number') {
    return hasValidJobDistance(value) ? value : undefined
  }
  if (typeof value === 'string' && value !== '') {
    const parsed = parseFloat(value)
    return hasValidJobDistance(parsed) ? parsed : undefined
  }
  return undefined
}

export interface EmploymentDistanceRecallFields extends EmployerAddressFields {
  jobDistance?: number
  distanceAddressSnapshot?: string
  address?: string
}

/** True when distance is missing or employer address changed since last distance was set. */
export function needsEmploymentDistanceRecall(
  employer: EmploymentDistanceRecallFields,
): boolean {
  const currentAddress = buildEmployerAddressString(employer)
  if (!currentAddress) {
    return false
  }

  if (!hasValidJobDistance(employer.jobDistance)) {
    return true
  }

  if (!employer.distanceAddressSnapshot) {
    return true
  }

  return (
    normalizeAddressForComparison(currentAddress) !==
    normalizeAddressForComparison(employer.distanceAddressSnapshot)
  )
}

export function distanceAddressSnapshotForEmployer(
  employer: EmployerAddressFields & { address?: string },
): string | undefined {
  const snapshot = buildEmployerAddressString(employer)
  return snapshot || undefined
}

async function geocodeAddress(address: string): Promise<google.maps.LatLng> {
  await waitForGoogleMaps()

  if (!window.google?.maps) {
    throw new Error('Google Maps API not loaded')
  }

  const geocoder = new google.maps.Geocoder()

  return new Promise((resolve, reject) => {
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results?.[0]?.geometry?.location) {
        resolve(results[0].geometry.location)
        return
      }

      reject(new Error(`Geocode failed: ${status}`))
    })
  })
}

async function calculateStraightLineDistance(
  origin: string,
  destination: string,
): Promise<number> {
  await waitForGoogleMaps()

  if (!window.google?.maps?.geometry?.spherical) {
    throw new Error('Google Maps geometry library not loaded')
  }

  const [originLatLng, destinationLatLng] = await Promise.all([
    geocodeAddress(origin),
    geocodeAddress(destination),
  ])

  const meters = google.maps.geometry.spherical.computeDistanceBetween(
    originLatLng,
    destinationLatLng,
  )

  return Math.round((meters / 1609.34) * 100) / 100
}

/**
 * Recalculate property-to-employer distance: driving route first, straight-line fallback.
 */
export async function recalculateEmploymentDistance(
  propertyAddress: string,
  employerAddress: string,
): Promise<EmploymentDistanceResult> {
  if (!propertyAddress?.trim() || !employerAddress?.trim()) {
    return { miles: null, method: 'none' }
  }

  try {
    const miles = await calculatePropertyToEmployerDistance(
      propertyAddress.trim(),
      employerAddress.trim(),
    )
    return { miles, method: 'driving' }
  } catch {
    try {
      const miles = await calculateStraightLineDistance(
        propertyAddress.trim(),
        employerAddress.trim(),
      )
      return { miles, method: 'straight_line' }
    } catch {
      return { miles: null, method: 'none' }
    }
  }
}
